/**
 * Small, allocation-light 2D geometry helpers. All functions are pure.
 * Polylines/polygons are flat number arrays [x0,y0,x1,y1,...].
 */
import type { Vec } from '../../shared/doc.ts';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const v = (x: number, y: number): Vec => ({ x, y });
export const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
export const mul = (a: Vec, s: number): Vec => ({ x: a.x * s, y: a.y * s });
export const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
export const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
export const len = (a: Vec): number => Math.hypot(a.x, a.y);
export const dist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
export const lerp = (a: Vec, b: Vec, t: number): Vec => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
export const norm = (a: Vec): Vec => {
  const l = len(a);
  return l === 0 ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
};
export const clamp = (n: number, lo: number, hi: number): number => (n < lo ? lo : n > hi ? hi : n);

export function rectCenter(r: Rect): Vec {
  return { x: r.x + r.w / 2, y: r.y + r.h / 2 };
}
export function rectContains(r: Rect, p: Vec): boolean {
  return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
}
export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
export function overlapArea(a: Rect, b: Rect): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}
export function inflate(r: Rect, d: number): Rect {
  return { x: r.x - d, y: r.y - d, w: r.w + 2 * d, h: r.h + 2 * d };
}
export function normalizeRect(a: Vec, b: Vec): Rect {
  return { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}
export function rectToPoly(r: Rect): number[] {
  return [r.x, r.y, r.x + r.w, r.y, r.x + r.w, r.y + r.h, r.x, r.y + r.h];
}
export function polyBounds(pts: number[]): Rect {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    const x = pts[i]!,
      y = pts[i + 1]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  if (minX === Infinity) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}
/** Area of a polygon (shoelace; always positive). */
export function polyArea(pts: number[]): number {
  const n = pts.length / 2;
  let a = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    a += pts[2 * i]! * pts[2 * j + 1]! - pts[2 * j]! * pts[2 * i + 1]!;
  }
  return Math.abs(a) / 2;
}
export function polyCentroid(pts: number[]): Vec {
  const n = pts.length / 2;
  if (n === 0) return { x: 0, y: 0 };
  if (n < 3) {
    let sx = 0,
      sy = 0;
    for (let i = 0; i < pts.length; i += 2) {
      sx += pts[i]!;
      sy += pts[i + 1]!;
    }
    return { x: sx / n, y: sy / n };
  }
  let a = 0,
    cx = 0,
    cy = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const x0 = pts[2 * i]!,
      y0 = pts[2 * i + 1]!,
      x1 = pts[2 * j]!,
      y1 = pts[2 * j + 1]!;
    const f = x0 * y1 - x1 * y0;
    a += f;
    cx += (x0 + x1) * f;
    cy += (y0 + y1) * f;
  }
  if (Math.abs(a) < 1e-9) {
    const b = polyBounds(pts);
    return rectCenter(b);
  }
  a *= 0.5;
  return { x: cx / (6 * a), y: cy / (6 * a) };
}
export function pointInPoly(p: Vec, pts: number[]): boolean {
  const n = pts.length / 2;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = pts[2 * i]!,
      yi = pts[2 * i + 1]!,
      xj = pts[2 * j]!,
      yj = pts[2 * j + 1]!;
    if (yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Nearest point on segment ab to p, with the parametric t in [0,1]. */
export function nearestOnSegment(p: Vec, a: Vec, b: Vec): { p: Vec; t: number; d: number } {
  const ab = sub(b, a);
  const l2 = dot(ab, ab);
  const t = l2 === 0 ? 0 : clamp(dot(sub(p, a), ab) / l2, 0, 1);
  const q = add(a, mul(ab, t));
  return { p: q, t, d: dist(p, q) };
}
export function distToSegment(p: Vec, a: Vec, b: Vec): number {
  return nearestOnSegment(p, a, b).d;
}
/** Nearest point on a flat polyline (optionally closed). */
export function nearestOnPolyline(p: Vec, pts: number[], closed: boolean): { p: Vec; d: number; seg: number } {
  const n = pts.length / 2;
  let best = { p: { x: pts[0] ?? 0, y: pts[1] ?? 0 }, d: Infinity, seg: 0 };
  if (n === 1) return { ...best, d: dist(p, best.p) };
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const j = (i + 1) % n;
    const r = nearestOnSegment(p, { x: pts[2 * i]!, y: pts[2 * i + 1]! }, { x: pts[2 * j]!, y: pts[2 * j + 1]! });
    if (r.d < best.d) best = { p: r.p, d: r.d, seg: i };
  }
  return best;
}

/** Proper segment intersection test (excluding collinear touching). */
export function segmentsIntersect(a: Vec, b: Vec, c: Vec, d: Vec): boolean {
  const d1 = cross(sub(d, c), sub(a, c));
  const d2 = cross(sub(d, c), sub(b, c));
  const d3 = cross(sub(b, a), sub(c, a));
  const d4 = cross(sub(b, a), sub(d, a));
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}
/**
 * Does a segment pass through a rectangle: some length of it inside (Liang–Barsky clipping — a
 * segment through two opposite corners is inside all the way; one only touching a corner is not).
 */
export function segmentIntersectsRect(a: Vec, b: Vec, r: Rect): boolean {
  if (rectContains(r, a) || rectContains(r, b)) return true;
  const dx = b.x - a.x,
    dy = b.y - a.y;
  let t0 = 0,
    t1 = 1;
  const clip = (p: number, q: number): boolean => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  if (!clip(-dx, a.x - r.x) || !clip(dx, r.x + r.w - a.x) || !clip(-dy, a.y - r.y) || !clip(dy, r.y + r.h - a.y)) return false;
  return t1 - t0 > 1e-9;
}
/** Distance from a point to a circle's edge (negative inside). */
export function segmentDistToPoint(a: Vec, b: Vec, p: Vec): number {
  return distToSegment(p, a, b);
}

/**
 * Intersection of the ray from the rect centre towards `toward` with the
 * boundary of a rounded rectangle. Used to attach leaders cleanly.
 */
export function roundedRectEdgePoint(r: Rect, radius: number, toward: Vec): Vec {
  const c = rectCenter(r);
  const d = sub(toward, c);
  if (Math.abs(d.x) < 1e-9 && Math.abs(d.y) < 1e-9) return { x: c.x, y: r.y };
  const hw = r.w / 2,
    hh = r.h / 2;
  const rad = Math.min(radius, hw, hh);
  // Scale factor to hit the axis-aligned box.
  const tx = Math.abs(d.x) < 1e-9 ? Infinity : hw / Math.abs(d.x);
  const ty = Math.abs(d.y) < 1e-9 ? Infinity : hh / Math.abs(d.y);
  const t = Math.min(tx, ty);
  const hit = add(c, mul(d, t));
  // If the hit point lies in a corner region, intersect with the corner circle instead.
  const lx = Math.abs(hit.x - c.x),
    ly = Math.abs(hit.y - c.y);
  if (lx > hw - rad && ly > hh - rad && rad > 0) {
    const cc = { x: c.x + Math.sign(d.x) * (hw - rad), y: c.y + Math.sign(d.y) * (hh - rad) };
    // Ray: c + d*s, circle centre cc radius rad. Solve |c + d s - cc|^2 = rad^2.
    const f = sub(c, cc);
    const A = dot(d, d),
      B = 2 * dot(f, d),
      C = dot(f, f) - rad * rad;
    const disc = B * B - 4 * A * C;
    if (disc >= 0) {
      const s = (-B + Math.sqrt(disc)) / (2 * A);
      return add(c, mul(d, s));
    }
  }
  return hit;
}

/* ------------------------------------------------------------------------ */
/* Polyline processing                                                      */
/* ------------------------------------------------------------------------ */

/** Ramer–Douglas–Peucker simplification (flat array in/out). */
export function simplify(pts: number[], eps: number): number[] {
  const n = pts.length / 2;
  if (n < 3) return pts.slice();
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    const a = { x: pts[2 * s]!, y: pts[2 * s + 1]! };
    const b = { x: pts[2 * e]!, y: pts[2 * e + 1]! };
    let maxD = -1,
      idx = -1;
    for (let i = s + 1; i < e; i++) {
      const d = distToSegment({ x: pts[2 * i]!, y: pts[2 * i + 1]! }, a, b);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > eps && idx > 0) {
      keep[idx] = 1;
      stack.push([s, idx], [idx, e]);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[2 * i]!, pts[2 * i + 1]!);
  return out;
}

/** Chaikin corner cutting; keeps endpoints for open polylines. */
export function chaikin(pts: number[], iterations = 2, closed = false): number[] {
  let cur = pts;
  for (let it = 0; it < iterations; it++) {
    const n = cur.length / 2;
    if (n < 3) return cur;
    const out: number[] = [];
    if (!closed) out.push(cur[0]!, cur[1]!);
    const segs = closed ? n : n - 1;
    for (let i = 0; i < segs; i++) {
      const j = (i + 1) % n;
      const x0 = cur[2 * i]!,
        y0 = cur[2 * i + 1]!,
        x1 = cur[2 * j]!,
        y1 = cur[2 * j + 1]!;
      out.push(x0 * 0.75 + x1 * 0.25, y0 * 0.75 + y1 * 0.25, x0 * 0.25 + x1 * 0.75, y0 * 0.25 + y1 * 0.75);
    }
    if (!closed) out.push(cur[2 * n - 2]!, cur[2 * n - 1]!);
    cur = out;
  }
  return cur;
}

/** Drop consecutive points closer than `minStep`. */
export function dedupe(pts: number[], minStep: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) {
    const x = pts[i]!,
      y = pts[i + 1]!;
    const n = out.length;
    if (n === 0 || Math.hypot(x - out[n - 2]!, y - out[n - 1]!) >= minStep) out.push(x, y);
  }
  // Always keep the true last point.
  if (pts.length >= 2 && out.length >= 2) {
    const lx = pts[pts.length - 2]!,
      ly = pts[pts.length - 1]!;
    if (out[out.length - 2] !== lx || out[out.length - 1] !== ly) out.push(lx, ly);
  }
  return out;
}

export function polylineLength(pts: number[], closed = false): number {
  let l = 0;
  const n = pts.length / 2;
  const segs = closed ? n : n - 1;
  for (let i = 0; i < segs; i++) {
    const j = (i + 1) % n;
    l += Math.hypot(pts[2 * j]! - pts[2 * i]!, pts[2 * j + 1]! - pts[2 * i + 1]!);
  }
  return l;
}

/** Point at a fraction t (0..1) along an open polyline. */
export function pointAlong(pts: number[], t: number): Vec {
  const total = polylineLength(pts);
  let target = clamp(t, 0, 1) * total;
  const n = pts.length / 2;
  for (let i = 0; i < n - 1; i++) {
    const a = { x: pts[2 * i]!, y: pts[2 * i + 1]! };
    const b = { x: pts[2 * i + 2]!, y: pts[2 * i + 3]! };
    const l = dist(a, b);
    if (target <= l || i === n - 2) return l === 0 ? a : lerp(a, b, target / l);
    target -= l;
  }
  return { x: pts[0] ?? 0, y: pts[1] ?? 0 };
}

export function translatePts(pts: number[], dx: number, dy: number): number[] {
  const out = new Array<number>(pts.length);
  for (let i = 0; i < pts.length; i += 2) {
    out[i] = pts[i]! + dx;
    out[i + 1] = pts[i + 1]! + dy;
  }
  return out;
}

/**
 * A point moving from `from` to `to` that may not enter any of the polygons: where it
 * ends up — `to` when that is outside them all, else the point where the way there
 * first meets a polygon's border (a hair before it), or `from` when no border is met
 * (the way starts inside already).
 */
export function stopAtBorder(from: Vec, to: Vec, polys: readonly number[][]): Vec {
  if (!polys.some((p) => pointInPoly(to, p))) return to;
  let best = Infinity;
  for (const p of polys) {
    const n = p.length / 2;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const a = { x: p[2 * j]!, y: p[2 * j + 1]! },
        b = { x: p[2 * i]!, y: p[2 * i + 1]! };
      const t = segmentHit(from, to, a, b);
      if (t !== null && t < best) best = t;
    }
  }
  if (best === Infinity) return from;
  const t = Math.max(0, best - 1e-3);
  return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
}
/** Where the segment p→q crosses the segment a→b, as a fraction of p→q; null when it does not. */
function segmentHit(p: Vec, q: Vec, a: Vec, b: Vec): number | null {
  const r = sub(q, p),
    s = sub(b, a);
  const den = cross(r, s);
  if (Math.abs(den) < 1e-12) return null;
  const qp = sub(a, p);
  const t = cross(qp, s) / den;
  const u = cross(qp, r) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? t : null;
}
